import { faTable } from "@fortawesome/pro-regular-svg-icons";
import { isObject } from "lodash";
import React from "react";

import { DisplayDateTime } from "src/base-components/Date";
import {
  PropertiesList,
  PropertiesListItem,
} from "src/base-components/PropertiesList";
import { CaseBlock, CaseBlockProps } from "src/cases/caseView/CaseBlock";
import { JSONValueViewer } from "src/dataTable/DetailedView/JSONValueViewer";
import { EmptyState } from "src/design-system/EmptyState";
import { MultilineDataValue } from "src/manualReview/reviewCaseCommon/Card";
import { isISO8601 } from "src/utils/datetime";
import { speakPythonPrimitive } from "src/utils/speakPython";
import { isProbablyMarkdown } from "src/utils/stringUtils";

type TableBlockData = {
  record: {
    [key: string]: unknown;
  };
};

type TableBlockProps = Omit<CaseBlockProps, "children"> & {
  data: TableBlockData;
};

const renderValue = (value: unknown, key: string): React.ReactNode => {
  if (typeof value === "number") {
    return <span className="font-code-12">{value}</span>;
  }

  if (typeof value === "boolean" || value === null) {
    return speakPythonPrimitive(String(value));
  }

  if (Array.isArray(value) || isObject(value)) {
    return <JSONValueViewer field={key} value={value} />;
  }

  if (typeof value === "string" && isISO8601(value)) {
    return <DisplayDateTime value={value} />;
  }

  if (typeof value === "string" && isProbablyMarkdown(value)) {
    return <MultilineDataValue fieldName={key} value={value} />;
  }

  return String(value);
};

export const TableBlock: React.FC<TableBlockProps> = ({ data, ...props }) => {
  const items: PropertiesListItem[] = Object.entries(data.record).map(
    ([key, value]) => ({
      key,
      label: key,
      value: renderValue(value, key),
    }),
  );

  if (items.length === 0) {
    return (
      <CaseBlock {...props}>
        <EmptyState
          description="This table has no data to display"
          headline="No data available"
          icon={faTable}
          size="sm"
        />
      </CaseBlock>
    );
  }

  return (
    <CaseBlock {...props}>
      <PropertiesList
        dataLocPrefix="table-block"
        items={items}
        rightAlignValues={true}
        showIcons={false}
      />
    </CaseBlock>
  );
};
